/**
 * Shop Theme Context Provider
 * Manages and applies shop theme across entire application
 * Scalable for future additions (shop image, fonts, etc.)
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useCurrentShop } from '@app/core';

/**
 * Complete theme configuration
 * Scalable structure for future additions:
 * - colors: Primary, secondary colors
 * - images: Shop logo, banner image, etc.
 * - fonts: Custom fonts (future)
 * - spacing: Custom spacing values (future)
 * - typography: Font sizes, weights (future)
 */
export interface ShopAppTheme {
  colors: {
    primary: string;      // Primary color (banner, buttons)
    secondary: string;    // Secondary color (backgrounds, accents)
  };
  images?: {
    logo?: string;        // Shop logo (base64 or URL)
    banner?: string;      // Banner image (base64 or URL)
    favicon?: string;     // Favicon (future)
  };
  lookAndFeel?: string;   // Additional styling preference
  [key: string]: any;     // Extensible for future properties
}

/**
 * Default theme (fallback)
 */
const DEFAULT_THEME: ShopAppTheme = {
  colors: {
    primary: '#007AFF',   // Blue
    secondary: '#F5F5F5', // Light Gray
  },
  images: {},
  lookAndFeel: '#FF9500', // Orange accent
};

/**
 * Theme context type
 */
interface ThemeContextType {
  theme: ShopAppTheme;
  applyingTheme: boolean;
  updateTheme: (newTheme: Partial<ShopAppTheme>) => void;
}

/**
 * Create theme context
 */
const ShopThemeContext = createContext<ThemeContextType>({
  theme: DEFAULT_THEME,
  applyingTheme: false,
  updateTheme: () => {},
});

/**
 * Theme Provider Component
 * Wraps around entire app and provides shop theme
 */
export const ShopThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const shop = useCurrentShop();
  const [theme, setTheme] = useState<ShopAppTheme>(DEFAULT_THEME);
  const [applyingTheme, setApplyingTheme] = useState(false);

  /**
   * Load shop theme when shop data updates
   * Triggers on any shop property change to ensure theme stays in sync
   * Handles both initial load and subsequent updates
   */
  useEffect(() => {
    if (shop?.theme) {
      console.log('🔄 Shop theme detected, applying...', shop.theme);
      applyShopTheme(shop.theme);
    } else if (shop && !shop.theme) {
      // Shop exists but has no theme - use default
      console.log('⚠️ Shop has no theme, using default');
      setTheme(DEFAULT_THEME);
      applyThemeToDOM(DEFAULT_THEME);
    }
    // Removed "waiting for shop data" log - it's expected on login page
  }, [shop?.id, shop?.theme, shop?.name, shop?.colors, shop]);

  /**
   * Apply shop theme to application
   * Scalable design allows for future enhancements
   */
  const applyShopTheme = (shopTheme: any) => {
    try {
      setApplyingTheme(true);

      const newTheme: ShopAppTheme = {
        colors: {
          primary: shopTheme.colors?.primary || DEFAULT_THEME.colors.primary,
          secondary: shopTheme.colors?.secondary || DEFAULT_THEME.colors.secondary,
        },
        images: {
          logo: shopTheme.images?.logo,
          banner: shopTheme.images?.banner,
          favicon: shopTheme.images?.favicon,
        },
        lookAndFeel: shopTheme.lookAndFeel || DEFAULT_THEME.lookAndFeel,
      };

      // Log theme application for debugging
      console.log('🎨 Applying shop theme:', newTheme.colors);

      // Apply theme to CSS custom properties (for web)
      applyThemeToDOM(newTheme);

      setTheme(newTheme);
    } catch (error) {
      console.error('Failed to apply shop theme:', error);
      setTheme(DEFAULT_THEME);
    } finally {
      setApplyingTheme(false);
    }
  };

  /**
   * Apply theme to DOM using CSS custom properties
   * Scalable for future CSS-based theming
   */
  const applyThemeToDOM = (appTheme: ShopAppTheme) => {
    if (typeof window === 'undefined') return;

    const root = document.documentElement;

    // Apply color theme
    root.style.setProperty('--theme-primary', appTheme.colors.primary);
    root.style.setProperty('--theme-secondary', appTheme.colors.secondary);
    root.style.setProperty('--theme-accent', appTheme.lookAndFeel);

    console.log('✅ Theme applied to DOM:', {
      primary: appTheme.colors.primary,
      secondary: appTheme.colors.secondary,
      accent: appTheme.lookAndFeel,
    });

    // Apply images if available
    if (appTheme.images?.banner) {
      root.style.setProperty(
        '--theme-banner-image',
        `url(${appTheme.images.banner})`
      );
    }

    if (appTheme.images?.logo) {
      root.style.setProperty(
        '--theme-logo-image',
        `url(${appTheme.images.logo})`
      );
    }

    // Apply background color
    document.body.style.backgroundColor = appTheme.colors.secondary;
  };

  /**
   * Update theme programmatically
   * Useful for theme customization screens
   */
  const updateTheme = (newTheme: Partial<ShopAppTheme>) => {
    const mergedTheme: ShopAppTheme = {
      ...theme,
      ...newTheme,
      colors: {
        ...theme.colors,
        ...(newTheme.colors || {}),
      },
      images: {
        ...theme.images,
        ...(newTheme.images || {}),
      },
    };

    applyShopTheme(mergedTheme);
  };

  return (
    <ShopThemeContext.Provider
      value={{
        theme,
        applyingTheme,
        updateTheme,
      }}
    >
      {children}
    </ShopThemeContext.Provider>
  );
};

/**
 * Hook to use shop theme
 * Usage: const { theme } = useShopTheme();
 */
export const useShopTheme = (): ThemeContextType => {
  const context = useContext(ShopThemeContext);

  if (!context) {
    throw new Error('useShopTheme must be used within ShopThemeProvider');
  }

  return context;
};

/**
 * Hook to get specific theme colors
 * Usage: const colors = useThemeColors();
 */
export const useThemeColors = () => {
  const { theme } = useShopTheme();
  return theme.colors;
};

/**
 * Hook to get theme images
 * Usage: const images = useThemeImages();
 */
export const useThemeImages = () => {
  const { theme } = useShopTheme();
  return theme.images || {};
};

/**
 * Hook to get theme accent
 * Usage: const accent = useThemeAccent();
 */
export const useThemeAccent = () => {
  const { theme } = useShopTheme();
  return theme.lookAndFeel || theme.colors.primary;
};

export default ShopThemeProvider;

